import { createSelector } from 'reselect'
import { IStationState, OrderStatesEnum } from '../../constants/order'
import { StationColumnIds, StationTypesEnum } from '../../constants/stations'
import { TabsEnum } from '../../constants/tabs'
import { IOrder } from '../../types/order'
import { appSelectors } from '../reducers/app'
import { RootState } from '../store'

const selectFirebaseOrders = (
    stationCode?: string,
    stationStates?: IStationState[],
    states?: OrderStatesEnum[]
) => [
    ...(stationStates
        ? stationStates.map(
              (stationState) => (state: RootState) => state.order.stationState[stationState]
          )
        : []),
    ...(states && !stationStates
        ? states.map((orderState) => (state: RootState) => state.order.state[orderState])
        : []),
]

const selectOrders = (
    stationCode?: string,
    stationStates?: IStationState[],
    states?: OrderStatesEnum[]
) =>
    createSelector([...selectFirebaseOrders(stationCode, stationStates, states)], (...args) => {
        if (args.includes(undefined)) {
            return undefined
        }

        const flattenedOrders = args.flat() as IOrder[]

        const keysSet = flattenedOrders.reduce(
            (keys, { orderKey }) => keys.add(orderKey),
            new Set()
        )

        return flattenedOrders
            .filter((order) => {
                let statesConstraint = true

                if (stationStates && states) {
                    statesConstraint =
                        stationStates.includes(order.stations?.[stationCode!] as IStationState) &&
                        states.includes(order.state)
                }

                return statesConstraint && keysSet.delete(order.orderKey)
            })
            .sort((a, b) => {
                const deliveryDateA = a.dateTime.date ?? a.dateTime.since ?? a.dateTime.until
                const deliveryDateB = b.dateTime.date ?? b.dateTime.since ?? a.dateTime.until

                if (deliveryDateA === deliveryDateB) {
                    return a.orderId < b.orderId ? -1 : 1
                }

                return deliveryDateA! < deliveryDateB! ? -1 : 1
            })
    })

export const selectFilteredOrders = (
    columnId: StationColumnIds,
    stationCode?: string,
    stationStates?: IStationState[],
    states?: OrderStatesEnum[]
) =>
    createSelector(
        [selectOrders(stationCode, stationStates, states), appSelectors.currentTab],
        (orders, currentTab) => {
            return orders
                ? orders.filter((order) => {
                      let tabConstraint = true

                      if (currentTab === TabsEnum.DELIVERY) {
                          tabConstraint = order.isAddressRequired
                      } else if (currentTab === TabsEnum.TAKE_AWAY) {
                          tabConstraint = !order.isAddressRequired
                      }

                      const pickerConstraint =
                          stationCode === StationTypesEnum.PICKER_STATION &&
                          columnId === StationColumnIds.PICKER_NEW
                              ? !(
                                    order.stations?.[StationTypesEnum.PICKER_STATION] ===
                                        OrderStatesEnum.COOKED &&
                                    order.state === OrderStatesEnum.IN_PROCESS
                                )
                              : true

                      return tabConstraint && pickerConstraint
                  })
                : orders
        }
    )
